import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductCategory } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './products.css';

/**
 * Bereich „Kategorien“ der Einrichtung unter „Produkte“ (KON-110). Hauptkategorien mit ihren Unterkategorien darunter, genau zwei Ebenen. Jede Hauptkategorie hat
 * ein eigenes Feld für neue Unterkategorien, sortiert wird jeweils innerhalb einer Ebene.
 */
export function ProductCategoriesPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const canWrite = can('setup.write');
  const canDelete = can('setup.delete');
  const categories = useQuery({ queryKey: ['product-categories', 'manage'], queryFn: async () => (await api<{ data: ProductCategory[] }>('GET', '/product-categories/manage')).data });
  const refresh = () => client.invalidateQueries({ queryKey: ['product-categories'] });

  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [subName, setSubName] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const message = (e: Error) => (e instanceof ApiError ? (e.status === 422 ? Object.values(e.fields)[0] ?? e.message : e.message) : 'Das hat nicht geklappt.');

  const create = useMutation({
    mutationFn: (input: { name: string; parentId: string | null }) => api('POST', '/product-categories', input),
    onSuccess: (_, input) => {
      if (input.parentId) setSubName((s) => ({ ...s, [input.parentId!]: '' }));
      else setName('');
      setError(null);
      refresh();
    },
    onError: (e) => setError(message(e)),
  });
  const rename = useMutation({
    mutationFn: (id: string) => api('PATCH', `/product-categories/${id}`, { name: editName.trim() }),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      refresh();
    },
    onError: (e) => setError(message(e)),
  });
  const reorder = useMutation({ mutationFn: (input: { parentId: string | null; ids: string[] }) => api('POST', '/product-categories/order', input), onSuccess: refresh, onError: (e) => setError(message(e)) });
  const remove = useMutation({ mutationFn: (id: string) => api('DELETE', `/product-categories/${id}`), onSuccess: refresh, onError: (e) => setError(message(e)) });

  const all = categories.data ?? [];
  const mains = all.filter((c) => !c.parentId);
  const childrenOf = (id: string) => all.filter((c) => c.parentId === id);
  const move = (siblings: ProductCategory[], index: number, delta: -1 | 1, parentId: string | null) => {
    const ids = siblings.map((c) => c.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    reorder.mutate({ parentId, ids });
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    if (name.trim()) create.mutate({ name: name.trim(), parentId: null });
  }

  const row = (c: ProductCategory, siblings: ProductCategory[], index: number) => (
    <div className={`category-row${c.parentId ? ' category-row--sub' : ''}`}>
      {editing === c.id ? (
        <form
          className="category-row__edit"
          onSubmit={(e) => {
            e.preventDefault();
            rename.mutate(c.id);
          }}
        >
          <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={80} aria-label={`Neue Bezeichnung für ${c.name}`} autoFocus />
          <button type="submit" className="btn btn--primary btn--sm" disabled={rename.isPending || editName.trim().length < 2}>Speichern</button>
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditing(null)}>Abbrechen</button>
        </form>
      ) : (
        <span className="category-row__name">
          {c.parentId && <span className="muted" aria-hidden="true">└ </span>}
          <strong>{c.name}</strong>
          <span className="muted small"> · {c.productCount === 1 ? '1 Produkt' : `${c.productCount} Produkte`}</span>
        </span>
      )}
      {editing !== c.id && (canWrite || canDelete) && (
        <div className="row-actions">
          {canWrite && (
            <>
              <button type="button" className="btn btn--secondary btn--sm" aria-label={`${c.name} nach oben`} disabled={index === 0 || reorder.isPending} onClick={() => move(siblings, index, -1, c.parentId)}>↑</button>
              <button type="button" className="btn btn--secondary btn--sm" aria-label={`${c.name} nach unten`} disabled={index === siblings.length - 1 || reorder.isPending} onClick={() => move(siblings, index, 1, c.parentId)}>↓</button>
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setEditing(c.id); setEditName(c.name); }}>Umbenennen</button>
            </>
          )}
          {canDelete && <button type="button" className="btn btn--danger btn--sm" disabled={remove.isPending} onClick={() => window.confirm(`Kategorie „${c.name}“ löschen?`) && remove.mutate(c.id)}>Löschen</button>}
        </div>
      )}
    </div>
  );

  return (
    <section className="card">
      <h2>Kategorien</h2>
      <p className="muted small">
        Haupt- und Unterkategorien für Ihre Produkte, zum Beispiel „Möbel“ mit „Stühle“ und „Tische“. Es gibt genau zwei Ebenen. Ein Produkt liegt in einer Haupt- oder
        Unterkategorie, und der Filter nach einer Hauptkategorie zeigt auch die Produkte ihrer Unterkategorien.
      </p>
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {canWrite && (
        <form className="category-new" onSubmit={submit} noValidate>
          <label className="field">
            <span>Neue Hauptkategorie</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Zum Beispiel Möbel" maxLength={80} />
          </label>
          <button className="btn btn--primary" type="submit" disabled={create.isPending || name.trim().length < 2}>Hauptkategorie anlegen</button>
        </form>
      )}

      {categories.isLoading && <p className="muted">Wird geladen …</p>}
      {!categories.isLoading && mains.length === 0 && <p className="empty">Noch keine Kategorien. Produkte funktionieren auch ohne.</p>}
      <ul className="category-tree">
        {mains.map((main, mi) => {
          const subs = childrenOf(main.id);
          return (
            <li key={main.id} className="category-tree__main">
              {row(main, mains, mi)}
              {subs.length > 0 && (
                <ul className="category-tree__subs">
                  {subs.map((sub, si) => <li key={sub.id}>{row(sub, subs, si)}</li>)}
                </ul>
              )}
              {canWrite && (
                <form
                  className="category-tree__add"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const value = (subName[main.id] ?? '').trim();
                    if (value) create.mutate({ name: value, parentId: main.id });
                  }}
                >
                  <input className="input" value={subName[main.id] ?? ''} onChange={(e) => setSubName((s) => ({ ...s, [main.id]: e.target.value }))} placeholder={`Unterkategorie zu ${main.name}`} maxLength={80} aria-label={`Neue Unterkategorie zu ${main.name}`} />
                  <button type="submit" className="btn btn--secondary btn--sm" disabled={create.isPending || (subName[main.id] ?? '').trim().length < 2}>Hinzufügen</button>
                </form>
              )}
            </li>
          );
        })}
      </ul>
      <p className="muted small">Eine Kategorie lässt sich nur löschen, wenn ihr keine Produkte und keine Unterkategorien zugeordnet sind.</p>
    </section>
  );
}
